import { logger } from "@tecma/logs-fe";
import {
  Survey,
  SurveyResponses,
} from "modules/survey/domain/survey/survey.types";
import { patchBulkSurveysResponses } from "modules/survey/infrastructure/store/saga/survey.api";
import { surveySelectors } from "modules/survey/infrastructure/store/slice/survey.selectors";
import { applicationStateSelectors } from "store/slices/applicationState/selectors";
import { authStateSelectors } from "store/slices/auth/selectors";
import { environmentSelectors } from "store/slices/environment/selector";
import { useAppSelector } from "store/store.hooks";

const useSurveyInfo = () => {
  const surveys = useAppSelector(surveySelectors.surveys);
  const token = useAppSelector(authStateSelectors.token);
  const clientId = useAppSelector(authStateSelectors.clientID);
  const userSurveysIds = useAppSelector(authStateSelectors.userSurveysIds);
  const userSurveys = useAppSelector(authStateSelectors.userSurveys);
  const surveysIds = useAppSelector(surveySelectors.surveysIds);
  const environment = useAppSelector(environmentSelectors.environment);
  const projectId = useAppSelector(applicationStateSelectors.projectID);
  const completedLoggedSurveys = surveys.filter((survey) =>
    userSurveysIds?.includes(survey.id),
  );
  const roomSelectorUserSurvey = userSurveys?.filter(
    (userSurvey) =>
      surveys.find((survey) => survey.id === userSurvey.survey_id)?.id,
  );

  const orderedCompletedLoggedSurveys = completedLoggedSurveys.sort(
    (a, b) => a.order - b.order,
  );
  const questionaireToResume =
    orderedCompletedLoggedSurveys[orderedCompletedLoggedSurveys.length - 1];

  const hasCompletedAllLoggedSurveys = surveysIds?.every((surveyId) =>
    userSurveysIds?.includes(surveyId),
  );

  const unloggedSurveysCompleted = surveys.filter((survey) =>
    localStorage.getItem(`surveyResponses-${survey.id}`),
  );
  const orderedUnloggedSurveysCompleted = unloggedSurveysCompleted.sort(
    (a, b) => a.order - b.order,
  );
  const lastUnloggedSurveyCompleted =
    orderedUnloggedSurveysCompleted[orderedUnloggedSurveysCompleted.length - 1];
  const hasCompletedAllUnloggedSurveys =
    orderedUnloggedSurveysCompleted.length === surveys.length;

  const localSurveyResponses: Array<SurveyResponses> = surveys.map((survey) =>
    JSON.parse(localStorage.getItem(`surveyResponses-${survey.id}`) ?? "{}"),
  );

  const lastLocalSurvey = localSurveyResponses.find(
    (response) => lastUnloggedSurveyCompleted?.order === response?.surveyOrder,
  );

  const lastUnloggedQuestion = lastLocalSurvey?.questionsStatus
    ? lastLocalSurvey?.questionsStatus[
        (lastLocalSurvey?.questionsStatus.length || 0) - 1
      ]
    : null;

  const sendUnloggedSurveysResponses = async () => {
    if (!hasCompletedAllUnloggedSurveys) return;
    try {
      const response = await patchBulkSurveysResponses({
        client_id: clientId ?? "",
        survey_id: orderedUnloggedSurveysCompleted[0].id,
        surveysResponses: localSurveyResponses as unknown as Survey[],
        project_Id: projectId ?? "",
        token: token?.accessToken ?? "",
        baseUri: environment.REACT_APP_API_REST_BASE_URI,
      });
      if (response.status === "success") {
        orderedUnloggedSurveysCompleted.forEach((survey) => {
          localStorage.removeItem(`surveyResponses-${survey.id}`);
        });
      }
    } catch (error) {
      logger.error(error);
    }
  };

  return {
    questionaireToResume,
    hasCompletedAllLoggedSurveys,
    lastUnloggedSurveyCompleted,
    sendUnloggedSurveysResponses,
    hasCompletedAllUnloggedSurveys,
    lastUnloggedQuestion,
    completedLoggedSurveys,
    roomSelectorUserSurvey,
  };
};

export default useSurveyInfo;
